import { isOrganizationAdminRole } from "./organization-members.js";
import { z } from "zod";
const sha256Schema = z.string().regex(/^[a-f0-9]{64}$/u);
export const RESOLVER_EVAL_F0_CATALOG_HASH = "959181174cc36736450780b7547c9042093266ebd0cc2ce4e743febdf8ab74c2";
export const RESOLVER_EVAL_F0_PROJECTION_HASH = "f52adb2f1f22da42c8482374cfbfee5f9c0b7ed1040e3769bbb2309e4a52d4fa";
export const RESOLVER_EVAL_F0_PROJECTION_CHARS = 16_525;
export const RESOLVER_EVAL_F0_SELECTIONS = [
    ..."ABCDEFGHIJKLMNOPQRSTUVWXYZ", "AA", "AB", "AC", "AD", "AE", "AF",
];
export const RESOLVER_EVAL_F0_NONE_SELECTION = "A";
export const RESOLVER_EVAL_RECEIPT_SCHEMA_SHA256 = "db825cd252dc00e10790a2e7baefd3b546adda34e72cb7bf405b3ac723f31845";
export const RESOLVER_EVAL_F0_TOOL_NAME = "submit_contract_plan";
export const RESOLVER_EVAL_F0_TOOL_DESCRIPTION = "Submit exactly one opaque contract selection or the explicit no-contract selection.";
export const resolverEvalModeSchema = z.literal("resolver_eval");
export function resolverEvalTextContainsContractId(value) {
    return /\bcap\.[A-Za-z0-9_.-]+(?:@\d+)?\b/iu.test(value);
}
export function canonicalResolverEvalJson(value) {
    if (Array.isArray(value))
        return `[${value.map(canonicalResolverEvalJson).join(",")}]`;
    if (value && typeof value === "object") {
        return `{${Object.entries(value).sort(([left], [right]) => left.localeCompare(right))
            .map(([key, child]) => `${JSON.stringify(key)}:${canonicalResolverEvalJson(child)}`)
            .join(",")}}`;
    }
    const json = JSON.stringify(value);
    if (typeof json !== "string")
        throw new Error("Resolver receipt contains a non-JSON value");
    return json;
}
export function resolverEvalZstdWireBytes(json, compress, compressionLevelParameter) {
    const compressed = compress(json, { params: { [compressionLevelParameter]: 3 } });
    return new Uint8Array(compressed.buffer, compressed.byteOffset, compressed.byteLength);
}
export const resolverEvalOpaqueQuerySchema = z.string().min(1).max(100_000).refine((value) => !resolverEvalTextContainsContractId(value), "resolver evaluation query must not contain semantic contract identifiers");
export const resolverEvalPinSchema = z.object({
    mode: resolverEvalModeSchema, catalogHash: sha256Schema,
}).strict();
export const resolverEvalRuntimeIdentitySchema = z.object({
    version: z.literal(1),
    sessionId: z.string().trim().min(1).max(200),
    runId: z.string().trim().min(1).max(200),
}).strict();
export const resolverEvalEnvelopeSchema = z.object({
    mode: resolverEvalModeSchema,
    catalogHash: sha256Schema,
    projectionHash: sha256Schema,
    projection: z.string().min(1),
    projectionChars: z.number().int().positive(),
    projectionBytes: z.number().int().positive(),
    noneSelection: z.string().regex(/^[A-Z]{1,3}$/u),
    candidates: z.array(z.object({
        selection: z.string().regex(/^[A-Z]{1,3}$/u),
        contractRef: z.string().trim().min(1).nullable(),
    }).strict()).min(2),
}).strict().superRefine((value, context) => {
    if (value.projection.length !== value.projectionChars) {
        context.addIssue({ code: "custom", message: "resolver projection character length mismatch" });
    }
    if (new TextEncoder().encode(value.projection).length !== value.projectionBytes) {
        context.addIssue({ code: "custom", message: "resolver projection byte length mismatch" });
    }
    const selections = new Set(value.candidates.map((candidate) => candidate.selection));
    if (selections.size !== value.candidates.length || !selections.has(value.noneSelection)) {
        context.addIssue({ code: "custom", message: "resolver candidates must be unique and include none" });
    }
});
export function resolverEvalProjectionHashBytes(value) {
    const envelope = resolverEvalEnvelopeSchema.parse(value);
    const refs = envelope.candidates.map(({ contractRef }) => contractRef ?? "__none__").sort();
    const selectionByRef = new Map(envelope.candidates.map(({ contractRef, selection }) => [contractRef ?? "__none__", selection]));
    if (selectionByRef.size !== envelope.candidates.length) {
        throw new Error("Resolver evaluation candidates must map unique contract references");
    }
    const hashInput = ["canonical", "routing_complete", "canonical", envelope.projection]
        .concat(refs.map((ref) => `${ref}= ${selectionByRef.get(ref)}`)).join("\0");
    return new TextEncoder().encode(hashInput);
}
function sealedResolverEvalEnvelope(value, computedProjectionHash) {
    const envelope = resolverEvalEnvelopeSchema.parse(value);
    const selectionByRef = new Map(envelope.candidates.map(({ contractRef, selection }) => [contractRef ?? "__none__", selection]));
    if (envelope.catalogHash !== RESOLVER_EVAL_F0_CATALOG_HASH ||
        envelope.projectionHash !== RESOLVER_EVAL_F0_PROJECTION_HASH ||
        envelope.projectionChars !== RESOLVER_EVAL_F0_PROJECTION_CHARS ||
        envelope.projectionBytes !== RESOLVER_EVAL_F0_PROJECTION_CHARS ||
        envelope.noneSelection !== RESOLVER_EVAL_F0_NONE_SELECTION ||
        JSON.stringify(envelope.candidates.map(({ selection }) => selection)) !==
            JSON.stringify(RESOLVER_EVAL_F0_SELECTIONS) ||
        selectionByRef.size !== envelope.candidates.length ||
        selectionByRef.get("__none__") !== RESOLVER_EVAL_F0_NONE_SELECTION ||
        computedProjectionHash !== RESOLVER_EVAL_F0_PROJECTION_HASH) {
        throw new Error("Resolver evaluation envelope does not match the sealed F0 request");
    }
    return envelope;
}
function resolverEvalF0SystemPrompt(envelope) {
    return [
        "You are a selection-only governed contract resolver.",
        "Use only the opaque catalog below. Do not infer or output semantic contract identifiers.",
        "Call submit_contract_plan exactly once with one opaque selection, abstained, and a reason.",
        "Set abstained=true only for the no-contract selection. Do not answer outside the tool.",
        `Catalog hash: ${envelope.catalogHash}`,
        `Projection hash: ${envelope.projectionHash}`,
        `Projection characters: ${envelope.projectionChars}; bytes: ${envelope.projectionBytes}`,
        "",
        envelope.projection,
    ].join("\n");
}
/** Pure F0 request constructor shared by control and Electric hash attestations. */
export function buildResolverEvalSealedRequest(value, query, computedProjectionHash) {
    const envelope = sealedResolverEvalEnvelope(value, computedProjectionHash);
    const currentQuery = z.string().min(1).max(100_000)
        .refine((value) => !resolverEvalTextContainsContractId(value), "resolver evaluation query must not contain semantic contract identifiers")
        .parse(query);
    return {
        model: "gpt-5.6-sol",
        store: false,
        stream: true,
        instructions: resolverEvalF0SystemPrompt(envelope),
        input: [{ role: "user", content: [{ type: "input_text", text: currentQuery }] }],
        text: { verbosity: "low" },
        include: ["reasoning.encrypted_content"],
        reasoning: { effort: "high", summary: "auto" },
        tools: [{
                type: "function",
                name: RESOLVER_EVAL_F0_TOOL_NAME,
                description: RESOLVER_EVAL_F0_TOOL_DESCRIPTION,
                parameters: {
                    type: "object",
                    additionalProperties: false,
                    properties: {
                        selection: { type: "string", enum: [...RESOLVER_EVAL_F0_SELECTIONS] },
                        abstained: { type: "boolean" },
                        reason: { type: "string", minLength: 1, maxLength: 2_000 },
                    },
                    required: ["selection", "abstained", "reason"],
                },
                strict: true,
            }],
        tool_choice: "required",
        parallel_tool_calls: false,
    };
}
const receiptEvidenceValueSchema = (value) => z.union([
    z.object({ available: z.literal(true), value }).strict(),
    z.object({ available: z.literal(false), reason: z.string().min(1) }).strict(),
]);
const receiptUsageSchema = z.object({
    input: z.number().nonnegative(), output: z.number().nonnegative(),
    cacheRead: z.number().nonnegative(), cacheWrite: z.number().nonnegative(),
    cacheWrite1h: z.number().nonnegative().optional(), reasoning: z.number().nonnegative().optional(),
    totalTokens: z.number().nonnegative(),
}).strict();
const receiptOutcomeSchema = z.union([
    z.object({
        status: z.literal("completed"), selection: z.enum(RESOLVER_EVAL_F0_SELECTIONS),
        abstained: z.boolean(), reason: z.string().min(1).max(2_000).regex(/\S/u),
    }).strict(),
    z.object({
        status: z.literal("invalid"), scoredOutcome: z.literal("__invalid__"),
        code: z.enum([
            "forbidden_output", "blocked_continuation", "missing_sink", "duplicate_sink",
            "unknown_selection", "malformed_sink", "abstention_mismatch", "invalid_sink", "wrong_sink",
        ]),
    }).strict(),
    z.object({
        status: z.literal("error"), scoredOutcome: z.literal("__invalid__"),
        code: z.literal("provider_error"),
    }).strict(),
]);
export const resolverEvalReceiptSchema = z.object({
    schemaVersion: z.literal(2), runId: z.string().min(1).max(200).regex(/\S/u),
    turn: z.number().int().positive(), outcome: receiptOutcomeSchema,
    catalogHash: z.literal(RESOLVER_EVAL_F0_CATALOG_HASH),
    projectionHash: z.literal(RESOLVER_EVAL_F0_PROJECTION_HASH),
    projectionChars: z.literal(RESOLVER_EVAL_F0_PROJECTION_CHARS),
    projectionBytes: z.literal(RESOLVER_EVAL_F0_PROJECTION_CHARS),
    providerPayload: z.object({
        json: z.string().min(1), sha256: sha256Schema,
        chars: z.number().int().positive(), bytes: z.number().int().positive(),
        wireSha256: sha256Schema, wireBytes: z.number().int().positive(),
        wireContentEncoding: z.literal("zstd"),
        secretFree: z.literal(true), onPayloadObserved: z.boolean(),
    }).strict(),
    assistant: z.object({
        requestedModel: z.object({
            available: z.literal(true), value: z.literal("gpt-5.6-sol"),
        }).strict(),
        returnedModel: receiptEvidenceValueSchema(z.string().min(1)),
        responseId: receiptEvidenceValueSchema(z.string().min(1)),
        usage: receiptEvidenceValueSchema(receiptUsageSchema),
        stopReason: receiptEvidenceValueSchema(z.enum(["stop", "length", "toolUse", "error", "aborted"])),
        systemFingerprint: z.object({
            available: z.literal(false),
            reason: z.literal("pi-ai AssistantMessage does not expose systemFingerprint"),
        }).strict(),
    }).strict(),
    runtimeEvidence: z.object({
        provider: z.literal("openai-codex"), api: z.literal("openai-codex-responses"),
        transport: z.literal("sse"), model: z.literal("gpt-5.6-sol"),
        reasoning: z.literal("high"),
        outputLimit: z.literal("provider_managed_no_max_output_tokens"),
        authOrder: z.tuple([z.literal("codex_subscription")]), apiFallback: z.literal(false),
        providerMaxRetries: z.literal(0), modelMaxRetries: z.literal(0),
        billing: z.object({
            mode: z.literal("codex_subscription"), apiSpendUsd: z.literal(0),
        }).strict(),
        providerCalls: z.literal(1), providerToolCalls: z.number().int().nonnegative(),
        blockedContinuations: z.number().int().nonnegative(),
        sinkCalls: z.number().int().nonnegative(),
        contextPolicy: z.literal("stateless_current_query_only"),
        requestedCapabilities: z.tuple([z.literal("readonly-data")]),
        channelAccessBypass: z.literal(false),
    }).strict(),
}).strict().superRefine((value, context) => {
    try {
        JSON.parse(value.providerPayload.json);
    }
    catch {
        context.addIssue({ code: "custom", path: ["providerPayload", "json"],
            message: "resolver provider payload must be valid JSON" });
    }
    if (value.providerPayload.json.length !== value.providerPayload.chars) {
        context.addIssue({ code: "custom", path: ["providerPayload", "chars"],
            message: "resolver provider payload character length mismatch" });
    }
    if (new TextEncoder().encode(value.providerPayload.json).length !== value.providerPayload.bytes) {
        context.addIssue({ code: "custom", path: ["providerPayload", "bytes"],
            message: "resolver provider payload byte length mismatch" });
    }
    const outcome = value.outcome;
    const evidence = value.runtimeEvidence;
    if (outcome.status === "completed") {
        if (!value.providerPayload.onPayloadObserved ||
            evidence.providerToolCalls !== 1 || evidence.sinkCalls !== 1 ||
            evidence.blockedContinuations !== 0) {
            context.addIssue({ code: "custom", path: ["runtimeEvidence"],
                message: "completed resolver receipts require exactly one sink and no blocked continuation" });
        }
        if (outcome.abstained !== (outcome.selection === RESOLVER_EVAL_F0_NONE_SELECTION)) {
            context.addIssue({ code: "custom", path: ["outcome", "abstained"],
                message: "resolver abstention must match the sealed none selection" });
        }
        if (resolverEvalTextContainsContractId(outcome.reason)) {
            context.addIssue({ code: "custom", path: ["outcome", "reason"],
                message: "resolver outcome reason must remain opaque" });
        }
    }
    else if (outcome.status === "invalid") {
        const oneSinkCodes = new Set([
            "unknown_selection", "malformed_sink", "abstention_mismatch", "invalid_sink",
        ]);
        if ((outcome.code === "missing_sink" &&
            (evidence.providerToolCalls !== 0 || evidence.sinkCalls !== 0)) ||
            (outcome.code === "duplicate_sink" &&
                evidence.providerToolCalls < 2 && evidence.sinkCalls < 2) ||
            (outcome.code === "wrong_sink" &&
                (evidence.providerToolCalls !== 1 || evidence.sinkCalls !== 0)) ||
            (outcome.code === "blocked_continuation" && evidence.blockedContinuations < 1) ||
            (oneSinkCodes.has(outcome.code) &&
                (evidence.providerToolCalls !== 1 || evidence.sinkCalls !== 1))) {
            context.addIssue({ code: "custom", path: ["runtimeEvidence"],
                message: "resolver invalid outcome is inconsistent with observed sink/continuation counts" });
        }
    }
});
export function assertResolverEvalReceiptIntegrity(value, computedPayloadSha256) {
    const receipt = resolverEvalReceiptSchema.parse(value);
    if (computedPayloadSha256 !== receipt.providerPayload.sha256) {
        throw new Error("Resolver evaluation provider payload hash mismatch");
    }
    return receipt;
}
export function resolverEvalFeatureConfig(env) {
    const enabled = env.MLX_RESOLVER_EVAL_ENABLED?.trim().toLowerCase();
    if (!enabled || enabled === "false")
        return { status: "disabled" };
    if (enabled !== "true")
        return { status: "misconfigured", reason: "enabled must be true or false" };
    const catalogHash = env.MLX_RESOLVER_EVAL_CATALOG_HASH?.trim().toLowerCase() ?? "";
    if (!sha256Schema.safeParse(catalogHash).success) {
        return { status: "misconfigured", reason: "enabled requires a 64-hex catalog hash" };
    }
    return catalogHash === RESOLVER_EVAL_F0_CATALOG_HASH
        ? { status: "enabled", catalogHash }
        : { status: "misconfigured", reason: "configured catalog does not match the sealed F0 treatment" };
}
export function isResolverEvalAdminRole(role) {
    return isOrganizationAdminRole(role);
}
